import { getAvatarProps } from '@AppModels/AvatarUtils';
import { Avatar } from '@HoudiniUI/Avatar';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { styleSheet } from './styles';

const MAX_SIGNPOST_USES = 3;

const CHEVRON_WIDTH = 13;
const CHEVRON_HEIGHT = 8;

const readStorage = (key: string) => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

const writeStorage = (key: string, value: string) => {
	try {
		window.localStorage.setItem(key, value);
	} catch {}
};

interface INavUserMenuContext {
	isMenuOpen: boolean;
	/** The sidebar avatar menu opened or closed itself; opening it directly means the user found its new home. */
	onSidebarMenuOpenChange: (open: boolean) => void;
	openMenu: () => void;
	recordSignpostUse: () => void;
	showSignpost: boolean;
}

const NavUserMenuContext = React.createContext<INavUserMenuContext | null>(null);

/**
 * Holds the sidebar user menu's open state so the signpost where the menu used to live can open it.
 * `storageKey` scopes the per-browser "has found the new menu" memory to one app.
 */
export const NavUserMenuProvider = ({ children, storageKey }: { children: React.ReactNode; storageKey: string }) => {
	const learnedKey = `${storageKey}:learned:v1`;
	const usesKey = `${storageKey}:signpost-uses:v1`;
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const [showSignpost, setShowSignpost] = React.useState(() => readStorage(learnedKey) !== 'true');

	const retireSignpost = () => {
		writeStorage(learnedKey, 'true');
		setShowSignpost(false);
	};

	const value: INavUserMenuContext = {
		isMenuOpen,
		onSidebarMenuOpenChange: open => {
			setIsMenuOpen(open);
			if (open) {
				retireSignpost();
			}
		},
		openMenu: () => setIsMenuOpen(true),
		recordSignpostUse: () => {
			const uses = Number(readStorage(usesKey) ?? 0) + 1;
			writeStorage(usesKey, String(uses));
			if (uses >= MAX_SIGNPOST_USES) {
				retireSignpost();
			}
		},
		showSignpost,
	};

	return <NavUserMenuContext.Provider value={value}>{children}</NavUserMenuContext.Provider>;
};

export const useNavUserMenu = () => {
	const context = React.useContext(NavUserMenuContext);
	if (!context) {
		throw new Error('useNavUserMenu must be used within a NavUserMenuProvider');
	}
	return context;
};

/** Sits where the account menu used to be and points people to its new home in the sidebar until they learn it. */
const _NavUserMenuSignpost = ({
	chevronColor,
	description,
	styles: overrides = [],
}: {
	/** The old trigger's chevron colour; the icon's own grey when omitted. */
	chevronColor?: string;
	description: string;
	styles?: StyleDeclarationValue[];
}) => {
	const userSession = useUserSession();
	const { openMenu, recordSignpostUse, showSignpost } = useNavUserMenu();

	if (!showSignpost) {
		return null;
	}

	const onClick = () => {
		openMenu();
		recordSignpostUse();
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					type='button'
					variant='ghost'
					aria-label='Account menu, now in the sidebar'
					onClick={onClick}
					styles={[styleSheet.signpost, ...overrides]}
				>
					<Avatar {...getAvatarProps(userSession.user)} styles={[styleSheet.signpostAvatar]} smallText={true} />
					{/* Wrapped so Button's 16x16 direct-child svg sizing leaves the old trigger's 13x8 chevron alone. */}
					<span className={css(styleSheet.signpostChevron)}>
						<DisclosureIcon type='chevron' fillColor={chevronColor} height={CHEVRON_HEIGHT} width={CHEVRON_WIDTH} />
					</span>
				</Button>
			</TooltipTrigger>
			<TooltipContent side='bottom'>{description}</TooltipContent>
		</Tooltip>
	);
};

export const NavUserMenuSignpost = observer(_NavUserMenuSignpost);
